import * as React from 'react';
import { Helmet } from 'react-helmet';
import UserSession from '@app/common/user-session';
import UserMgmtApi from '@app/api/user-api/user.actions';
import ExclamationCircleIcon from '@patternfly/react-icons/dist/js/icons/exclamation-circle-icon';
import { Saver } from '@app/components/Saver';
import { PasswordPolicy } from '@app/components/PasswordPolicy';
import { textInputRequiredOnly, textInputValidate, validatePassword } from '@app/lib/validator';
import { Button, Card, CardBody, PageSection, Title, TextInput, Flex, FlexItem, FormGroup as PFFormGroup } from '@patternfly/react-core';
import { AuthenticationDetails, CognitoUserPool, CognitoUser } from "amazon-cognito-identity-js";
import { Form, Card as StrapCard, CardBody as StrapCardBody, CardHeader, Col, Row, Label, Input, FormGroup } from 'reactstrap';
import VideoBackground from '@app/components/VideoBackground';
import { actionCreators as sessionActionCreators } from '@app/common/session/session.reducer';

interface ResetPasswordState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
  message: string;
  messageType: string;
  formSubmitted: boolean;
  isLoading: boolean;
}

interface ResetPasswordProps {
  hideVideoBackground?: boolean;
  compact?: boolean;
  dispatch?: any;
}

class ResetPassword extends React.Component<ResetPasswordProps & any, ResetPasswordState> {
  constructor(props) {
    super(props);

    this.state = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      message: "",
      messageType: "",
      formSubmitted: false,
      isLoading: false
    };
  }

  handleChange = (key: keyof ResetPasswordState, value: string) => {
    this.setState((prevState) => ({
      ...prevState,
      [key]: value,
      message: ""
    }));
  };

  handleSubmit = async () => {
    const { oldPassword, newPassword, confirmPassword } = this.state;
    const passwordValidator = validatePassword(newPassword, confirmPassword);

    if (passwordValidator.isValid === true) {
      this.setState(() => ({ 
        isLoading: true
      }));
      await this.verifyCurrentPassword(oldPassword, newPassword);
    }
    else {
      this.setState(() => ({ 
        message: passwordValidator.error,
        isLoading: false
      }));
      
      // Add Snackbar notification if dispatch is available (when used in profile page)
      if (this.props.dispatch) {
        this.props.dispatch(sessionActionCreators.notificationEnqueue({
          message: passwordValidator.error,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }));
      }
    }
  };

  verifyCurrentPassword = async (oldPassword, newPassword) => {
    const userName = UserSession.getParam('userName');
    const poolId = process.env.REACT_APP_USER_POOL_ID;
    const clientId = process.env.REACT_APP_POOL_WEB_CLI_ID;
    const pool = new CognitoUserPool({ UserPoolId: poolId, ClientId: clientId });
    const authDetails = new AuthenticationDetails({ Username: userName, Password: oldPassword });
    const cognitoUser = new CognitoUser({ Username: userName, Pool: pool });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: async (result) => {
        await this.resetPassword(oldPassword, newPassword); 
      },
      onFailure: async (error) => {
        this.setState(() => ({ 
          message: "Current password is invalid. Please try again",
          oldPassword: "",
          isLoading: false
        }));
        
        // Add Snackbar notification if dispatch is available (when used in profile page)
        if (this.props.dispatch) {
          this.props.dispatch(sessionActionCreators.notificationEnqueue({
            message: "Current password is invalid. Please try again",
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'error',
            },
          }));
        }
      }
    });
  }

  resetPassword = async (oldPassword, newPassword) => {
    const userId = UserSession.getParam("userId");
    const accessToken = UserSession.getParam("accessToken");
    const payload = {
      old_password: oldPassword,
      new_password: newPassword,
      access_token: accessToken,
      partner_id: UserSession.getPartnerId(),
      user_id: userId
    }

    try {
      let result = await UserMgmtApi.resetPassword(userId, payload);

      if (result?.status === 202) {
        this.setState(() => ({
          message: "Password was updated successfully!",
          formSubmitted: true,
          isLoading: false,
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        
        // Add Snackbar notification if dispatch is available (when used in profile page)
        if (this.props.dispatch) {
          this.props.dispatch(sessionActionCreators.notificationEnqueue({
            message: "Password was updated successfully!",
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'success',
            },
          }));
        }
      }
      else {
        this.setState(() => ({
          message: "There was an error updating your password",
          formSubmitted: true,
          isLoading: false,
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        
        // Add Snackbar notification if dispatch is available (when used in profile page)
        if (this.props.dispatch) {
          this.props.dispatch(sessionActionCreators.notificationEnqueue({
            message: "There was an error updating your password",
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'error',
            },
          }));
        }
      }
    }
    catch (error) {
      console.log(error);
      this.setState(() => ({
        message: "There was an error updating your password",
        isLoading: false
      }));
      
      // Add Snackbar notification if dispatch is available (when used in profile page)
      if (this.props.dispatch) {
        this.props.dispatch(sessionActionCreators.notificationEnqueue({
          message: "There was an error updating your password",
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }));
      }
    }
  }

  render() {
    const { oldPassword, newPassword, confirmPassword, formSubmitted, isLoading } = this.state;
    const passwordErrorState = newPassword ? textInputRequiredOnly(newPassword) : { validated: 'default', errorText: "" };
    const confirmPasswordErrorState = confirmPassword ? textInputValidate(confirmPassword, true, newPassword === confirmPassword, "Passwords must match") : { validated: 'default', errorText: "" };
    const formValid = oldPassword && newPassword && confirmPassword && (newPassword === confirmPassword);

    return (
      <React.Fragment>
        {!this.props.compact && (
          <Helmet>
            <title>Reset Password</title>
          </Helmet>
        )}
        {this.props.compact ? (
          <div className="login-form h-100 d-flex flex-column" style={{paddingBottom: '0.25rem'}}>
            <Row className="g-1 flex-grow-1">
              <Col md={7} className="d-flex flex-column">
                <PFFormGroup
                  label="Current Password"
                  isRequired
                  fieldId="oldPassword"
                  className="form-item mb-1"
                >
                  <TextInput
                    isRequired
                    type="password"
                    id="oldPassword"
                    name="oldPassword"
                    value={oldPassword}
                    onChange={(e) => this.handleChange("oldPassword", e)}
                    className="form-control form-control-sm"
                  />
                </PFFormGroup>
                <PFFormGroup
                  label="New Password"
                  isRequired
                  fieldId="newPassword"
                  className="form-item mb-1"
                >
                  <TextInput
                    isRequired
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => this.handleChange("newPassword", e)}
                    className="form-control form-control-sm"
                  />
                </PFFormGroup>
                <PFFormGroup
                  label="Confirm Password"
                  isRequired
                  fieldId="confirmPassword"
                  className="form-item mb-1"
                >
                  <TextInput
                    isRequired
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => this.handleChange("confirmPassword", e)}
                    className="form-control form-control-sm"
                  />
                </PFFormGroup>
                <div className="d-flex justify-content-center w-100 mt-auto" style={{paddingTop: '1rem'}}>
                  <Saver 
                    submitButtonText="Submit" 
                    submitButtonisDisabled={!formValid}
                    submit={async () => this.handleSubmit()}
                    showCancel={false}
                    showConfirmationMessage={false}
                    isLoading={isLoading}
                  />
                </div>
              </Col>
              <Col md={5} className="d-flex flex-column">
                <div style={{fontSize: '0.75rem', paddingLeft: '0.5rem', height: '100%'}}>
                  <PasswordPolicy />
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <Row className="h-100 w-100 login-page overflow-x-hidden">
            { !this.props.hideVideoBackground && (
              <Col lg={8} md={8} sm={12} xs={12} className="bg-white">
                <div>
                  <VideoBackground hideWelcomeText={true}/>
                </div>
              </Col>
            )}
            <Col lg={this.props.hideVideoBackground ? 12 : 4} md={this.props.hideVideoBackground ? 12 : 4} sm={12} xs={12} className="bg-white align-content-center">
              <Row>
                <Col lg={8} md={8} sm={8} xs={8} className="m-auto">
                  <div className="login-form pb-3">
                    <Form>
                      <FormGroup className="mb-3">
                        <Label for="oldPassword">Current Password</Label>
                        <Input
                          type="password"
                          id="oldPassword"
                          value={oldPassword}
                          onChange={(e) => this.handleChange("oldPassword", e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup className="mb-3">
                        <Label for="newPassword">New Password</Label>
                        <Input
                          type="password"
                          id="newPassword"
                          value={newPassword}
                          onChange={(e) => this.handleChange("newPassword", e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup className="mb-3">
                        <Label for="confirmPassword">Confirm Password</Label>
                        <Input
                          type="password"
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => this.handleChange("confirmPassword", e.target.value)}
                        />
                      </FormGroup>
                      <div className="mb-4">
                        <PasswordPolicy />
                      </div>
                      <div className="d-flex justify-content-center w-100 py-2">
                        <Saver 
                          submitButtonText="Submit" 
                          submitButtonisDisabled={!formValid}
                          submit={async () => this.handleSubmit()}
                          showCancel={false}
                          showConfirmationMessage={false}
                          isLoading={isLoading}
                        />
                      </div>
                    </Form>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </React.Fragment>
    );
  }
}

export default ResetPassword;